import React from "react";
import { QuestionItem } from "../../../types/form";
import LgvDateField from "../../../components/LgvDateField";
import CheckboxFieldRenderer from "./CheckboxFieldRenderer";
import LgvRadioGroup from "../../../components/LgvRadioGroup";
import LgvSelectField from "../../../components/LgvSelectField";
import LgvTextField from "../../../components/LgvTextField";
import { getDateBoundsForInput } from "../../../utils/validationRules";
import {
  normalizeQuestionRadioOptions,
  shouldUseTwoOptionRadioCards,
} from "../../../utils/radioOptions";
import { normalizeQuestionSelectOptions } from "../../../utils/selectOptions";
import YesNoFieldRenderer from "./YesNoFieldRenderer";

interface StandardFieldRendererProps {
  question: QuestionItem;
  questionType: string;
  inputName: string;
  currentValue: any;
  formData?: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  handleMultipleEntryChange: (
    inputName: string,
    idx: number,
    value: any,
  ) => void;
  handleAddEntry: (inputName: string) => void;
  handleRemoveEntry: (inputName: string, entryIdx: number) => void;
  onOpenHintsWithHint?: (
    hint: { title?: string; content: string } | null,
  ) => void;
  readOnly?: boolean;
}

const StandardFieldRenderer: React.FC<StandardFieldRendererProps> = ({
  question,
  questionType,
  inputName,
  currentValue,
  formData,
  setFormData,
  handleChange,
  handleMultipleEntryChange,
  handleAddEntry,
  handleRemoveEntry,
  onOpenHintsWithHint,
  readOnly = false,
}) => {
  const dateBounds =
    questionType === "date" ? getDateBoundsForInput(question, formData) : {};
  if (["radio", "yes_no", "boolean"].includes(questionType)) {
    const options = normalizeQuestionRadioOptions(question.options);

    if (questionType === "yes_no" || questionType === "boolean") {
      return (
        <YesNoFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (shouldUseTwoOptionRadioCards(options)) {
      return (
        <YesNoFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          options={options}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    return (
      <LgvRadioGroup
        name={inputName}
        value={currentValue}
        options={options}
        disabled={readOnly}
        required={question.isRequired}
        onChange={(value) =>
          setFormData((prev) => ({
            ...prev,
            [inputName]: value,
          }))
        }
        onOptionHintClick={
          onOpenHintsWithHint
            ? (hint) => onOpenHintsWithHint(hint)
            : undefined
        }
      />
    );
  }

  // Select/Dropdown
  if (["select", "dropdown"].includes(questionType)) {
    return (
      <LgvSelectField
        id={inputName}
        name={inputName}
        value={String(currentValue || "")}
        options={normalizeQuestionSelectOptions(question.options)}
        disabled={readOnly}
        required={question.isRequired}
        placeholder={question.placeholder || "Select..."}
        onChange={(value) =>
          handleChange({
            target: { name: inputName, value },
          } as React.ChangeEvent<HTMLInputElement>)
        }
      />
    );
  }

  // Textarea
  if (questionType === "textarea" || questionType === "long_text") {
    if (question.allowMultipleAnswers) {
      const entriesArray = Array.isArray(currentValue)
        ? currentValue
        : [currentValue || ""];

      return (
        <div className="space-y-3">
          {entriesArray.map((entryValue, idx) => (
            <div key={idx} className="space-y-2">
              <LgvTextField
                value={String(entryValue || "")}
                multiline
                disabled={readOnly}
                placeholder={question.placeholder}
                onChange={(value) =>
                  handleMultipleEntryChange(inputName, idx, value)
                }
              />
              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => handleRemoveEntry(inputName, idx)}
                  disabled={readOnly}
                  className="px-3 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddEntry(inputName)}
            disabled={readOnly}
            className="px-4 py-2 bg-[#1B365D] text-white rounded-md hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Another
          </button>
        </div>
      );
    }

    return (
      <LgvTextField
        id={inputName}
        name={inputName}
        value={String(currentValue || "")}
        multiline
        disabled={readOnly}
        required={question.isRequired}
        placeholder={question.placeholder}
        onChange={(value) =>
          handleChange({
            target: { name: inputName, value },
          } as React.ChangeEvent<HTMLTextAreaElement>)
        }
      />
    );
  }

  // Checkbox
  if (questionType === "checkbox" || questionType === "checkbox_group") {
    return (
      <CheckboxFieldRenderer
        question={question}
        inputName={inputName}
        currentValue={currentValue}
        setFormData={setFormData}
        readOnly={readOnly}
        onOpenHintsWithHint={onOpenHintsWithHint}
      />
    );
  }

  // Default Input (text, date, number)
  if (question.allowMultipleAnswers) {
    const entriesArray = Array.isArray(currentValue)
      ? currentValue
      : [currentValue || ""];

    return (
      <div className="space-y-3">
        {entriesArray.map((entryValue, idx) => (
          <div key={idx} className="flex items-center gap-2">
            {questionType === "date" ? (
              <LgvDateField
                id={idx === 0 ? inputName : `${inputName}-${idx}`}
                value={String(entryValue || "")}
                min={dateBounds.min}
                max={dateBounds.max}
                disabled={readOnly}
                placeholder={question.placeholder}
                onChange={(value) =>
                  handleMultipleEntryChange(inputName, idx, value)
                }
                className="flex-1"
              />
            ) : (
              <LgvTextField
                value={String(entryValue || "")}
                type={questionType === "number" ? "number" : "text"}
                disabled={readOnly}
                placeholder={question.placeholder}
                onChange={(value) => {
                  handleMultipleEntryChange(
                    inputName,
                    idx,
                    questionType === "number"
                      ? value === ""
                        ? ""
                        : Number(value)
                      : value,
                  );
                }}
                className="flex-1"
              />
            )}
            {idx > 0 && (
              <button
                type="button"
                onClick={() => handleRemoveEntry(inputName, idx)}
                disabled={readOnly}
                className="px-3 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => handleAddEntry(inputName)}
          disabled={readOnly}
          className="px-4 py-2 bg-[#1B365D] text-white rounded-md hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Add Another
        </button>
      </div>
    );
  }

  if (questionType === "date") {
    return (
      <LgvDateField
        id={inputName}
        name={inputName}
        value={String(currentValue || "")}
        min={dateBounds.min}
        max={dateBounds.max}
        disabled={readOnly}
        placeholder={question.placeholder}
        onChange={(value) =>
          handleChange({
            target: { name: inputName, value },
          } as React.ChangeEvent<HTMLInputElement>)
        }
      />
    );
  }

  return (
    <LgvTextField
      id={inputName}
      name={inputName}
      type={questionType === "number" ? "number" : "text"}
      value={String(currentValue || "")}
      disabled={readOnly}
      required={question.isRequired}
      placeholder={question.placeholder}
      onChange={(value) =>
        handleChange({
          target: {
            name: inputName,
            value:
              questionType === "number"
                ? value === ""
                  ? ""
                  : Number(value)
                : value,
          },
        } as React.ChangeEvent<HTMLInputElement>)
      }
    />
  );
};

export default StandardFieldRenderer;
